import { type FC, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import FontAwesomeLight from "../../../app/Utilities/Platform/FontAwesome/FontAwesomeLight";
import { hideDialog } from "../../Actions/DialogActions";
import Button from "../../Common/Button";
import CopyButton from "../../Common/CopyButton/CopyButton";

interface Props {
    client: string;
}

export const ApplicationDetails: FC<Props> = (props) => {
    const [ loading, setLoading ] = useState<boolean>(false);
    const [ name, setName ] = useState<string>("");
    const [ redirect, setRedirect ] = useState<string>("");
    const [ clientId, setClientId ] = useState<string>("");
    const [ clientSecret, setClientSecret ] = useState<string>("");
    const [ oldSecret, setOldSecret ] = useState<string | null>(null);
    const [ oldSecretExpiry, setOldSecretExpiry ] = useState<string | null>(null);
    const [ embedded, setEmbedded ] = useState<string>("");
    const [ error, setError ] = useState<null | string>("");

    const dispatch = useDispatch();

    useEffect(() => {
        setLoading(true);

        fetch(`/application/${props.client}`, {
            credentials: "same-origin",
            headers    : new Headers({
                "Accept"      : "application/json",
                "Content-Type": "application/json",
            }),
        })
            .then((response) => {
                if(response.status !== 200) {
                    throw new Error("Invalid Response");
                }

                return response.json();
            })
            .then(result => {
                setName(result.client.name);
                setRedirect(result.client.redirect);
                setClientId(result.client.client_id);
                setClientSecret(result.client.secret);
                setOldSecret(result.client.old_secret ?? null);
                setOldSecretExpiry(result.client.old_secret_expires_at ?? null);
                setEmbedded(result.client.embedded);
            })
            .catch((err) => {
                console.error("API Documentation: Error loading application details", err);
                setError(err instanceof Error ? err.message : JSON.stringify(err));
            })
            .finally(() => {
                setLoading(false);
            });
    }, [ props.client ]);

    let content;
    let errorContent;

    if(loading) {
        content = (
            <div className="application-details loading">
                <FontAwesomeLight icon="spinner" pulse />
                Loading...
            </div>
        );
    } else {
        content = (
            <div className="application-details">
                <h4 className="font-bold">Application Name</h4>
                <code>{name}</code>
                <h4 className="font-bold">Redirect URI</h4>
                <code>{redirect}</code>
                {typeof embedded !== "undefined" && (
                    <>
                        <h4><b>Embedded API URL</b></h4>
                        <code>{embedded}</code>
                    </>
                )}
                <h4 className="font-bold">Client ID<CopyButton text={clientId} /></h4>
                <code>{clientId}</code>
                <h4 className="font-bold">Client Secret<CopyButton text={clientSecret} /></h4>
                <code>{clientSecret}</code>
                {oldSecret && (
                    <>
                        <h4 className="font-bold">Old Secret</h4>
                        <code>{oldSecret.padStart(40, "x")}</code>
                        <h4>Expires At</h4>
                        <code>{oldSecretExpiry}</code>
                    </>
                )}
            </div>
        );
    }

    if(error) {
        errorContent = (
            <div className="rounded-lg bg-red-500 shadow-md text-white/75 p-4 mb-4">
                {error}
            </div>
        );
    }

    return (
        <>
            {errorContent}
            {content}
            <div className="dialog-buttons">
                <Button
                    type="button"
                    classes={[ "normal" ]}
                    onClick={() => dispatch(hideDialog())}
                >
                    Done
                </Button>
            </div>
        </>
    );
};
